/**
 * Hashed-chunk staleness recovery.
 *
 * After a redeploy, Vite emits new content-hashed filenames under
 * `/assets/*`. A browser tab still running the previous bundle holds a
 * route graph that references the *old* hashes — clicking a lazy-loaded
 * route then fires a dynamic `import('@/modules/...')` for a file that no
 * longer exists, surfacing as either:
 *
 *   - `Failed to fetch dynamically imported module`   (navigation import)
 *   - `Importing a module script failed`               (Safari variant)
 *   - a `vite:preloadError` event                      (hover preload)
 *
 * We catch all three globally and force a one-shot hard reload, which
 * picks up the freshly deployed `index.html` along with its new hashes.
 *
 * A 60 s sessionStorage guard prevents reload loops if the chunk really is
 * missing (broken deploy, corrupted CDN object, …) — after one failed
 * reload we surface the error to the user instead of pegging the tab.
 */

const RELOAD_GUARD_KEY = '__chunk-reload-at';
const RELOAD_COOLDOWN_MS = 60_000;
const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;

function reloadOnce(): void {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
    if (Number.isFinite(last) && Date.now() - last < RELOAD_COOLDOWN_MS) {
      // Already tried — let the error propagate so the user sees a real
      // failure instead of a reload loop.
      return;
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // sessionStorage can throw in some sandboxed contexts; we still want to
    // reload at least once if it does.
  }
  window.location.reload();
}

export function installChunkReloadHandler(): void {
  // Vite-specific event for module preload failures (hover-triggered).
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    reloadOnce();
  });

  // Generic safety net for navigation-triggered `import()` rejections that
  // React.lazy doesn't catch synchronously.
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason as { message?: string } | string | null;
    const message =
      typeof reason === 'string' ? reason : (reason?.message ?? '');
    if (CHUNK_ERROR_PATTERN.test(message)) {
      event.preventDefault();
      reloadOnce();
    }
  });
}
